import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import API from '../utils/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || '';

export default function BarterTradesPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [proposingLocation, setProposingLocation] = useState(null);
  const [locationData, setLocationData] = useState({ name: '', address: '', method: 'meet_in_person' });

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    fetchTrades();
  }, [user]);

  const fetchTrades = async () => {
    try {
      const { data } = await API.get('/barters/trades');
      setTrades(data.trades || []);
    } catch (err) {
      console.error('Failed to fetch trades:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusLabel = (status) => {
    const map = {
      pending: '⏳ Waiting for response',
      location_proposed: '📍 Propose location',
      location_agreed: '✓ Location confirmed',
      shipping_proposed: '🚚 Shipping proposed',
      shipping_agreed: '✓ Ready to exchange',
      completed: '✅ Completed',
      cancelled: '✕ Cancelled'
    };
    return map[status] || status;
  };

  const handleProposeLocation = async (tradeId) => {
    if (!locationData.name.trim() || !locationData.address.trim()) {
      toast.error('Please fill in location details');
      return;
    }

    try {
      await API.patch(`/barters/trades/${tradeId}/propose-location`, {
        locationName: locationData.name,
        locationAddress: locationData.address,
        shippingMethod: locationData.method
      });
      toast.success('Location proposed!');
      setProposingLocation(null);
      setLocationData({ name: '', address: '', method: 'meet_in_person' });
      fetchTrades();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to propose location');
    }
  };

  const handleAgreeLocation = async (tradeId) => {
    try {
      await API.patch(`/barters/trades/${tradeId}/agree-location`);
      toast.success('Location agreed!');
      fetchTrades();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to agree on location');
    }
  };

  const handleComplete = async (tradeId) => {
    if (!window.confirm('Mark this trade as complete?')) return;
    try {
      await API.patch(`/barters/trades/${tradeId}/complete`);
      toast.success('Trade completed!');
      fetchTrades();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to complete trade');
    }
  };

  const handleCancel = async (tradeId) => {
    if (!window.confirm('Cancel this trade?')) return;
    try {
      await API.patch(`/barters/trades/${tradeId}/cancel`);
      toast.success('Trade cancelled');
      fetchTrades();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to cancel trade');
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 0' }}>
        <div style={{ fontSize: 40, marginBottom: 12 }} className="float-anim">🍒</div>
        <div style={{ fontSize: 14, color: 'var(--text-soft)' }}>Loading trades...</div>
      </div>
    );
  }

  if (!trades.length) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 40px', background: 'var(--cream)', borderRadius: 16 }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>🔄</div>
        <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: 20, fontWeight: 700, marginBottom: 8 }}>
          No active trades
        </h3>
        <p style={{ fontSize: 14, color: 'var(--text-soft)', marginBottom: 20 }}>
          Start trading by browsing barter items or listing your own.
        </p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
          <button className="btn btn-cherry" onClick={() => navigate('/barter')}>
            🔄 Browse Barter Items
          </button>
          <button className="btn btn-ghost" onClick={() => navigate('/barter/create')}>
            + List Item
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {trades.map(trade => (
        <div
          key={trade.id}
          style={{
            background: 'white',
            border: '1px solid var(--border)',
            borderRadius: 14,
            overflow: 'hidden'
          }}
        >
          {/* Summary */}
          <button
            onClick={() => setExpandedId(expandedId === trade.id ? null : trade.id)}
            style={{
              width: '100%',
              padding: '18px 20px',
              textAlign: 'left',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              gap: 16,
              alignItems: 'center',
              flexWrap: 'wrap'
            }}
          >
            {/* Items preview */}
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>
                {trade.initiator_id === user.id ? trade.responder_title : trade.initiator_title}
              </div>
              <div style={{ fontSize: 13, color: 'var(--text-soft)', marginBottom: 8 }}>
                {trade.initiator_id === user.id
                  ? `Trading with ${trade.responder_first_name}`
                  : `Trading with ${trade.initiator_first_name}`}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-soft)' }}>
                Started {format(new Date(trade.created_at), 'MMM d, yyyy')}
              </div>
            </div>

            {/* Status badge */}
            <div style={{
              background: trade.status === 'completed' ? '#E8F5E9' : trade.status === 'cancelled' ? '#FFEBEE' : '#E3F2FD',
              color: trade.status === 'completed' ? '#2D7A3A' : trade.status === 'cancelled' ? '#C0392B' : '#1A5276',
              padding: '6px 12px',
              borderRadius: 8,
              fontSize: 12,
              fontWeight: 700,
              whiteSpace: 'nowrap'
            }}>
              {getStatusLabel(trade.status)}
            </div>

            {/* Expand icon */}
            <div style={{ fontSize: 16 }}>
              {expandedId === trade.id ? '▼' : '▶'}
            </div>
          </button>

          {/* Expanded details */}
          {expandedId === trade.id && (
            <div style={{ padding: '16px 20px', borderTop: '1px solid var(--border)', background: '#FAFAFA' }}>
              {/* Items info */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-soft)', marginBottom: 8, textTransform: 'uppercase' }}>
                  Trade Items
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div style={{ background: 'white', padding: 12, borderRadius: 10, border: '1px solid var(--border)' }}>
                    <div style={{ fontSize: 12, color: 'var(--text-soft)', marginBottom: 4 }}>
                      {trade.initiator_id === user.id ? 'You offer' : 'They offer'}
                    </div>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{trade.initiator_title}</div>
                    {trade.initiator_image && (
                      <img
                        src={`${API_BASE}${trade.initiator_image}`}
                        alt=""
                        style={{ width: '100%', height: 80, objectFit: 'cover', borderRadius: 6, marginTop: 8 }}
                      />
                    )}
                  </div>

                  <div style={{ background: 'white', padding: 12, borderRadius: 10, border: '1px solid var(--border)' }}>
                    <div style={{ fontSize: 12, color: 'var(--text-soft)', marginBottom: 4 }}>
                      {trade.responder_id === user.id ? 'You offer' : 'They offer'}
                    </div>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{trade.responder_title}</div>
                    {trade.responder_image && (
                      <img
                        src={`${API_BASE}${trade.responder_image}`}
                        alt=""
                        style={{ width: '100%', height: 80, objectFit: 'cover', borderRadius: 6, marginTop: 8 }}
                      />
                    )}
                  </div>
                </div>
              </div>

              {/* Location section */}
              {trade.status !== 'cancelled' && trade.status !== 'completed' && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-soft)', marginBottom: 8, textTransform: 'uppercase' }}>
                    📍 Meetup Location
                  </div>

                  {trade.trade_location_name ? (
                    <div style={{ background: 'white', padding: 12, borderRadius: 10, border: '1px solid var(--border)', marginBottom: 12 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>{trade.trade_location_name}</div>
                      <div style={{ fontSize: 13, color: 'var(--text-soft)' }}>{trade.trade_location_address}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-soft)', marginTop: 4 }}>
                        {trade.initiator_confirmed && trade.responder_confirmed ? (
                          <span style={{ color: '#2D7A3A', fontWeight: 700 }}>✅ Both confirmed</span>
                        ) : (
                          <>
                            {trade.initiator_confirmed && <span>✓ Initiator confirmed · </span>}
                            {trade.responder_confirmed && <span>✓ Responder confirmed</span>}
                          </>
                        )}
                      </div>

                      {!trade.initiator_confirmed && trade.initiator_id === user.id && (
                        <button
                          onClick={() => handleAgreeLocation(trade.id)}
                          className="btn btn-cherry btn-sm"
                          style={{ marginTop: 8 }}
                        >
                          ✓ Confirm Location
                        </button>
                      )}

                      {!trade.responder_confirmed && trade.responder_id === user.id && (
                        <button
                          onClick={() => handleAgreeLocation(trade.id)}
                          className="btn btn-cherry btn-sm"
                          style={{ marginTop: 8 }}
                        >
                          ✓ Confirm Location
                        </button>
                      )}
                    </div>
                  ) : (
                    <div style={{ background: 'white', padding: 12, borderRadius: 10, border: '1px solid var(--border)' }}>
                      {proposingLocation === trade.id ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                          <input
                            type="text"
                            placeholder="Location name (e.g., Starbucks on Main)"
                            value={locationData.name}
                            onChange={e => setLocationData(p => ({ ...p, name: e.target.value }))}
                            className="form-input"
                            style={{ fontSize: 13 }}
                          />
                          <textarea
                            placeholder="Address"
                            value={locationData.address}
                            onChange={e => setLocationData(p => ({ ...p, address: e.target.value }))}
                            className="form-textarea"
                            style={{ fontSize: 13, minHeight: 60 }}
                          />
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button
                              onClick={() => handleProposeLocation(trade.id)}
                              className="btn btn-cherry btn-sm"
                            >
                              📍 Propose
                            </button>
                            <button
                              onClick={() => setProposingLocation(null)}
                              className="btn btn-ghost btn-sm"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => setProposingLocation(trade.id)}
                          className="btn btn-full"
                          style={{ background: '#FF9800', color: 'white', border: 'none' }}
                        >
                          📍 Propose Meetup Location
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Action buttons */}
              {trade.status !== 'cancelled' && trade.status !== 'completed' && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {trade.initiator_confirmed && trade.responder_confirmed && (
                    <button
                      onClick={() => handleComplete(trade.id)}
                      className="btn btn-cherry btn-sm"
                    >
                      ✅ Mark Complete
                    </button>
                  )}
                  <button
                    onClick={() => handleCancel(trade.id)}
                    className="btn btn-sm"
                    style={{ background: '#FFEBEE', color: 'var(--cherry)', border: '1px solid rgba(192,57,43,.2)', borderRadius: 8, padding: '6px 14px', fontFamily: "'DM Sans',sans-serif", fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
                  >
                    ✕ Cancel
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
